import { fixedFloat, lerp, NumberSchema, type Interpable, type Lerpable } from '@utils';
import { composeStruct } from '@strings';
import { z } from 'zod';
import { Axis } from './Axis';

// #region Interfaces

export interface SizeObject {
  width: number;
  height: number;
}

// #endregion

// #region Schema

export const SizeSchema = z
  .array(NumberSchema, {
    message: 'Size must be an array of two numbers, [width, height]'
  })
  .length(2, { message: 'Size must have exactly 2 elements' })
  .transform(([width, height]) => size(width, height));

// #endregion

export function size(
  a:
    | number
    | [number, number]
    | { width: number; height: number }
    | { axis: Axis; major: number; minor: number }
    | Size
    | Float32Array = 0,
  b?: number
) {
  return new Size(a, b);
}

/**
 * Represents a 2D size with width and height.
 */
export class Size implements SizeObject, Lerpable, Interpable {
  readonly #width: number;
  readonly #height: number;

  // #region Constructors

  constructor(
    a:
      | number
      | [number, number]
      | { width: number; height: number }
      | { axis: Axis; major: number; minor: number }
      | Size
      | Float32Array = 0,
    b?: number
  ) {
    if (typeof a === 'number' && typeof b === 'number') {
      // a and b are numbers
      this.#width = a;
      this.#height = b;
    } else if (Array.isArray(a)) {
      // a is an array of two numbers
      this.#width = a[0];
      this.#height = a[1];
    } else if (a instanceof Size) {
      // a is a Size
      this.#width = a.width;
      this.#height = a.height;
    } else if (a instanceof Float32Array && a.length === 2) {
      // a is a Float32Array of two numbers
      this.#width = a[0];
      this.#height = a[1];
    } else if (typeof a === 'object') {
      // a is an object
      if ('width' in a && 'height' in a) {
        // a has width and height properties
        this.#width = a.width;
        this.#height = a.height;
      } else if ('axis' in a && 'major' in a && 'minor' in a) {
        // a has axis, major, and minor properties
        const { axis, major, minor } = a;
        if (axis === Axis.X) {
          this.#width = major;
          this.#height = minor;
        } else {
          this.#width = minor;
          this.#height = major;
        }
      } else {
        throw new Error('Invalid Size arguments');
      }
    } else if (typeof a === 'number' && b === undefined) {
      // a is a single number (default value of 0) and b is undefined
      this.#width = a;
      this.#height = a;
    } else {
      throw new Error('Invalid Size arguments');
    }
  }

  static ZERO = new Size(0, 0);

  // #region Attributes

  get width(): number {
    return this.#width;
  }

  get height(): number {
    return this.#height;
  }

  get isZero(): boolean {
    return this.width === 0 && this.height === 0;
  }

  get area(): number {
    return this.width * this.height;
  }

  get aspectRatio(): number {
    return this.width / this.height;
  }

  // #endregion

  // #region Interpolation

  lerp(t: number, end: this): this {
    return size(
      lerp(t, [this.width, end.width]),
      lerp(t, [this.height, end.height])
    ) as this;
  }

  interp(end: this): (t: number) => this {
    return (t: number) => this.lerp(t, end);
  }

  // #endregion

  // #region Comparison

  equals(other: Size | SizeObject): boolean {
    if (this === other) return true;
    return this.width === other.width && this.height === other.height;
  }

  // #endregion

  // #region Type Conversion

  get object(): SizeObject {
    return { width: this.width, height: this.height };
  }

  get array(): number[] {
    return [this.width, this.height];
  }

  get typedArray(): Float32Array {
    return new Float32Array([this.width, this.height]);
  }

  // #endregion

  // #region Immutable Setters

  withWidth(newWidth: number): Size {
    return size(newWidth, this.height);
  }

  withHeight(newHeight: number): Size {
    return size(this.width, newHeight);
  }

  // #endregion

  // #region Arithmetic Operations

  add(other: Size | SizeObject): Size {
    return size(this.width + other.width, this.height + other.height);
  }

  sub(other: Size | SizeObject): Size {
    return size(this.width - other.width, this.height - other.height);
  }

  mul(scalar: number): Size {
    return size(this.width * scalar, this.height * scalar);
  }

  div(scalar: number): Size {
    return size(this.width / scalar, this.height / scalar);
  }

  min(other: Size | SizeObject): Size {
    return size(Math.min(this.width, other.width), Math.min(this.height, other.height));
  }

  max(other: Size | SizeObject): Size {
    return size(Math.max(this.width, other.width), Math.max(this.height, other.height));
  }

  rounded(fractionDigits = 0): Size {
    return size(
      fixedFloat(this.width, fractionDigits),
      fixedFloat(this.height, fractionDigits)
    );
  }

  majorAxis(axis: Axis): number {
    switch (axis) {
      case Axis.X:
        return this.width;
      case Axis.Y:
        return this.height;
    }
  }

  minorAxis(axis: Axis): number {
    switch (axis) {
      case Axis.X:
        return this.height;
      case Axis.Y:
        return this.width;
    }
  }

  // #endregion

  // #region Serialization

  toJSON(): unknown {
    return [this.width, this.height];
  }

  static fromJSON(json: unknown): Size {
    if (!Array.isArray(json) || json.length !== 2) {
      throw new Error('Invalid Size JSON, expected array of length 2');
    }
    if (!json.every(x => typeof x === 'number')) {
      throw new Error('Invalid Size JSON, expected array of numbers');
    }
    const [width, height] = json;
    return size(width, height);
  }

  // #endregion

  // #region String Conversion

  /**
   * Formats the `Size` as a string for debugging.
   */
  toString(fractionDigits?: number): string {
    return composeStruct(
      [
        ['w', this.width],
        ['h', this.height]
      ],
      fractionDigits
    );
  }
}
